import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PriceMap } from '../types';
import { isStockOrETF, yahooTicker } from '../utils/stocks';
import { convertToUsd } from './useExchangeRates';

const CACHE_KEY = 'mycryptoplanner_stock_prices';
const CACHE_TTL = 60_000;

interface StockCacheEntry {
  usd: number;
  usd_24h_change: number;
  fetchedAt: number;
}

// cache keyed by ticker (e.g. "AAPL"), not full ID
type StockCache = Record<string, StockCacheEntry>;

function loadCache(): StockCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as StockCache) : {};
  } catch { return {}; }
}

function saveCache(cache: StockCache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
}

/** Fetch a single ticker's price via our serverless Yahoo proxy (/api/stock-quote).
 *  The proxy fetches Yahoo's v8 chart endpoint server-side (Yahoo blocks the direct
 *  browser call with CORS / a crumb-cookie 401); the response shape is unchanged.
 *
 *  Yahoo returns prices in each ticker's NATIVE currency (USD for US listings like
 *  NVDA/SPY, but AUD for ASX listings like VAS.AX / STW.AX). The app stores prices
 *  internally in USD, so we read meta.currency and convert any non-USD price via the
 *  app's existing FX rates. If the currency is missing or unconvertible, we throw
 *  rather than store a mis-denominated number (prefer no price over a wrong one). */
export async function fetchStockSpot(ticker: string): Promise<{ usd: number; usd_24h_change: number }> {
  const { data } = await axios.get('/api/stock-quote', {
    params: { ticker },
    timeout: 10_000,
    headers: { Accept: 'application/json' },
  });
  const meta = data?.chart?.result?.[0]?.meta ?? {};
  const rawCurrent   = (meta.regularMarketPrice ?? 0) as number;
  const rawPrevClose = (meta.chartPreviousClose ?? meta.previousClose ?? rawCurrent) as number;

  const currency = typeof meta.currency === 'string' ? meta.currency.toUpperCase() : '';
  if (!currency) {
    console.error(`[useStockPrices] ${ticker}: quote is missing a currency; skipping price to avoid storing a wrong value.`);
    throw new Error(`Quote for ${ticker} has no currency.`);
  }

  // Convert native → USD. `null` means we have no FX rate for this currency yet.
  const current = convertToUsd(rawCurrent, currency);
  if (current === null) {
    console.error(`[useStockPrices] ${ticker}: no FX rate to convert ${currency}→USD; skipping price to avoid storing a wrong value.`);
    throw new Error(`No FX rate to convert ${currency} to USD for ${ticker}.`);
  }
  // % change is currency-invariant; fall back to `current` if prevClose can't convert.
  const prevClose = convertToUsd(rawPrevClose, currency) ?? current;

  const usd_24h_change = prevClose > 0 ? ((current - prevClose) / prevClose) * 100 : 0;
  return { usd: current, usd_24h_change };
}

/** Convert cache + current assetIds to a PriceMap (keys are full IDs like stock:AAPL) */
function cacheToMap(cache: StockCache, assetIds: string[]): PriceMap {
  const map: PriceMap = {};
  for (const id of assetIds) {
    if (!isStockOrETF(id)) continue;
    const ticker = yahooTicker(id);
    const entry  = cache[ticker];
    if (entry) map[id] = { usd: entry.usd, usd_24h_change: entry.usd_24h_change };
  }
  return map;
}

export function useStockPrices(assetIds: string[]) {
  const stockIds   = assetIds.filter(isStockOrETF);
  const tickers    = stockIds.map(yahooTicker);
  const tickersKey = [...tickers].sort().join(',');

  const [prices,  setPrices]  = useState<PriceMap>(() => cacheToMap(loadCache(), assetIds));
  const [loading, setLoading] = useState(stockIds.length > 0);
  const [error,   setError]   = useState<string | null>(null);

  const fetchPrices = useCallback(async (force = false) => {
    if (tickers.length === 0) { setLoading(false); return; }

    const cache = loadCache();
    const now   = Date.now();
    const allFresh = tickers.every(
      (t) => cache[t] && now - cache[t].fetchedAt < CACHE_TTL
    );

    if (!force && allFresh) {
      setPrices(cacheToMap(cache, stockIds));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Fetch all tickers in parallel; settle individually so one failure doesn't block others
      const results = await Promise.allSettled(
        tickers.map(async (ticker) => {
          const price = await fetchStockSpot(ticker);
          return { ticker, ...price };
        })
      );

      const newCache = { ...loadCache() };
      for (const r of results) {
        if (r.status === 'fulfilled') {
          newCache[r.value.ticker] = {
            usd: r.value.usd,
            usd_24h_change: r.value.usd_24h_change,
            fetchedAt: now,
          };
        }
      }
      saveCache(newCache);
      setPrices(cacheToMap(newCache, stockIds));
    } catch {
      setPrices(cacheToMap(loadCache(), stockIds));
      setError('Unable to fetch live stock prices.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickersKey]);

  useEffect(() => {
    fetchPrices(false);
    const id = setInterval(() => fetchPrices(true), CACHE_TTL);
    return () => clearInterval(id);
  }, [tickersKey, fetchPrices]);

  return { prices, loading, error };
}
